import {
    AccountInvitation,
    DeleteAccountInvitationMutation,
    DeleteAccountInvitationMutationVariables,
    InviteUserToOrganizationMutation,
    InviteUserToOrganizationMutationVariables,
    ListAccountInvitationsByOrganizationIdQuery,
    ListAccountInvitationsByOrganizationIdQueryVariables,
    TeamType
} from "../../../API";
import {
    deleteAccountInvitation,
    inviteUserToOrganization
} from "../../../graphql/mutations";

import { API } from "aws-amplify";
import { AccountInvitationDAO } from "./AccountInvitationDAO";
import { AccountInvitationStatus } from "../../../models";
import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";
import { listAccountInvitationsByOrganizationId } from "../../../graphql/queries";

export class GraphQLAccountInvitationDAO implements AccountInvitationDAO {
    public static readonly GET_BY_ORGANIZATION_ID_ATTEMPT = "GraphQLAccountInvitationDAO.getAccountInvitationsByOrganizationId.Attempt";
    public static readonly GET_BY_ORGANIZATION_ID_FAILURE = "GraphQLAccountInvitationDAO.getAccountInvitationsByOrganizationId.Failure";
    public static readonly INVITE_USER_TO_ORGANIZATION_ATTEMPT = "GraphQLAccountInvitationDAO.inviteUserToOrganization.Attempt";
    public static readonly INVITE_USER_TO_ORGANIZATION_FAILURE = "GraphQLAccountInvitationDAO.inviteUserToOrganization.Failure";
    public static readonly REMOVE_INVITATION_ATTEMPT = "GraphQLAccountInvitationDAO.removeInvitation.Attempt";
    public static readonly REMOVE_INVITATION_FAILURE = "GraphQLAccountInvitationDAO.removeInvitation.Failure";

    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async getAccountInvitationsByOrganizationId(organizationId: string, status: AccountInvitationStatus): Promise<AccountInvitation[]> {
        try {
            this.logger.info(`Getting account invitations for organization ${organizationId}`, undefined, [GraphQLAccountInvitationDAO.GET_BY_ORGANIZATION_ID_ATTEMPT]);
            const variables: ListAccountInvitationsByOrganizationIdQueryVariables = {
                organizationId: organizationId,
                filter: {
                    status: {
                        eq: status
                    },
                    _deleted: {
                        ne: true
                    }
                }
            };

            const authToken = await this.idTokenSupplier.get();
            const result = await API.graphql({
                query: listAccountInvitationsByOrganizationId,
                variables,
                authToken
            }) as GraphQLResult<ListAccountInvitationsByOrganizationIdQuery>;

            return result.data?.listAccountInvitationsByOrganizationId?.items.filter((item): item is AccountInvitation => item != null) ?? [];
        } catch (error) {
            this.logger.error(`Error getting account invitations for organization ${organizationId}`, error, [GraphQLAccountInvitationDAO.GET_BY_ORGANIZATION_ID_FAILURE]);
            throw error;
        }
    }

    public async inviteUserToOrganization(organizationId: string, emails: string[], targetRole: TeamType): Promise<string> {
        try {
            const variables: InviteUserToOrganizationMutationVariables = {
                input: {
                    organizationId,
                    emails,
                    targetRole
                }
            };

            const authToken = await this.idTokenSupplier.get();
            const result = await API.graphql({
                query: inviteUserToOrganization,
                variables,
                authToken
            }) as GraphQLResult<InviteUserToOrganizationMutation>;

            return result.data?.inviteUserToOrganization ?? "";
        } catch (error) {
            this.logger.error(`Error inviting user to organization ${organizationId}`, error, [GraphQLAccountInvitationDAO.INVITE_USER_TO_ORGANIZATION_FAILURE]);
            throw error;
        }
    }

    public async removeInvitation(invitationId: string, version: number): Promise<boolean> {
        try {
            this.logger.info(`Removing invitation ${invitationId}`, undefined, [GraphQLAccountInvitationDAO.REMOVE_INVITATION_ATTEMPT]);
            const variables: DeleteAccountInvitationMutationVariables = {
                input: {
                    id: invitationId,
                    _version: version
                }
            };

            const authToken = await this.idTokenSupplier.get();
            const result = await API.graphql({
                query: deleteAccountInvitation,
                variables,
                authToken
            }) as GraphQLResult<DeleteAccountInvitationMutation>;
            return result.data?.deleteAccountInvitation != null;
        } catch (error) {
            this.logger.error(`Error removing invitation ${invitationId}`, error, [GraphQLAccountInvitationDAO.REMOVE_INVITATION_FAILURE]);
            throw error;
        }
    }
}

export const graphQLAccountInvitationDAO = new GraphQLAccountInvitationDAO(
    ClientLoggerFactory.getClientLogger("GraphQLAccountInvitationDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);
